import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BanknotesIcon } from '@heroicons/react/24/outline';

import Dialog from '@dashboard/components/Dialog';
import { Label } from '@dashboard/components/Form';
import { Form } from '@dashboard/components/Formik';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { chargeKey } from '@dashboard/lib/queryKeys';
import { formatPriceForInput, formatPriceInput } from '@dashboard/services/formatting/price';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';

import { Action } from './OrderActions';

export default function RecordPaymentAction({ payment, noun = 'payment' }) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const canRecord = payment.status === 'partially_paid' || payment.status === 'unpaid';

  const recordPaymentMutation = useMutation({
    mutationFn: values =>
      fetcher(`/charges/${payment._id}/record-payment`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: (charge, { amount }) => {
      queryClient.invalidateQueries({ queryKey: chargeKey(payment._id) });

      const recorded = getPriceLabel({ amount, currency: charge.currency });
      const due = getPriceLabel({ amount: charge.amountDue, currency: charge.currency });

      toast({
        title: 'Payment recorded',
        description:
          charge.amountDue > 0
            ? `${recorded} recorded. ${due} is still due.`
            : `${recorded} recorded. This ${noun} is now paid in full.`,
        type: 'success',
      });
    },
  });

  return (
    <>
      <Action
        icon={BanknotesIcon}
        label="Record payment"
        onClick={() => setOpen(true)}
        off={!canRecord}
        title="Nothing is due"
      />

      <Dialog
        title="Record payment"
        description="Recorded payments count towards your plan's monthly sales usage."
        open={open}
        onCancel={() => setOpen(false)}
        confirmButton={null}
        cancelButton={null}
      >
        {() => (
          <div>
            <div className="mb-4">
              <Label>Amount due</Label>
              <p className="font-semibold">
                {getPriceLabel({ amount: payment.amountDue, currency: payment.currency })}
              </p>
            </div>

            <Form
              initialValues={{
                amount: formatPriceForInput({
                  amount: payment.amountDue,
                  currency: payment.currency,
                  style: 'decimal',
                }),
              }}
              fields={[{ name: 'amount', label: 'Amount paid', type: 'number' }]}
              onSubmit={async values => {
                try {
                  const amount = formatPriceInput(values.amount, payment.currency);
                  await recordPaymentMutation.mutateAsync({ amount });
                  setOpen(false);
                  return {};
                } catch (error) {
                  return { error: error.message };
                }
              }}
              formSubmitAlign="end"
            />
          </div>
        )}
      </Dialog>
    </>
  );
}
